/* eslint camelcase: ["error", {allow:["rate_table","^LEGACY__*","^UNSAFE_*"]}] */
import React from 'react'
import Card from '../card'
import { contentTypeId } from '../../utils/contentful'
import { useEntity, useMeta } from '../helpers/selectors'
import sw from '../../utils/switch'
import dayjs from '../../utils/date'
import loadable from '@loadable/component'
import { DynamicSparkIcon } from '../helpers/dynamic-spark-icon'
import CardV2 from '../card-v2'

const SimpleCategoryGuideCard = loadable(() => import('./simple-category-guide-card'))

/** --- Legacy start
 * These page types are legacy contentful modules
 * that new pages still want to render and link to
 */
const LEGACY__uswitch = ({ heading, hero_image: img, meta_description: body, url }) => ({
  title: heading,
  description: body,
  linkHref: url,
  linkText: 'More info',
  image: img,
})

const LEGACY__bankrate = ({
  description,
  meta_description: metaDescription,
  wp_updated_date: date,
  hero_image: image,
  title,
  url,
}) => ({
  title,
  image,
  linkHref: `/uk/${url}`,
  description: metaDescription || description,
  linkText: dayjs(date).format('Do MMMM, YYYY'),
})

/** --- Legacy end */

/**
 * Page Type mappings are based on the `URL' model in contentful
 * https://github.com/uswitch/contentful-components/tree/master/models/url
 */
const page = ({ pageType, ...props }) =>
  sw({
    Review: reviewPage,
    default: guidePage,
  })(pageType, props)

const reviewPage = ({
  showDescription,
  metaDescription,
  showLink,
  linkText,
  metaImage,
  title,
  url,
}) => ({
  title,
  description: showDescription ? metaDescription : null,
  linkText: showLink ? linkText : null,
  linkHref: url,
  image: metaImage,
})

const guidePage = ({
  showDescription,
  metaDescription,
  showLink,
  linkText,
  metaImage,
  title,
  url,
}) => ({
  title,
  description: showDescription ? metaDescription : null,
  linkText: showLink ? linkText : null,
  linkHref: url,
  image: metaImage,
})

export const usePageProps = (props) => {
  const entityProps = useEntity(props.entity)
  const mappedProps = sw({
    richPage: page,
    modularPage: page,
    guide: LEGACY__uswitch,
    post: LEGACY__bankrate,
    rate_table: LEGACY__bankrate,
    calculator: LEGACY__bankrate,
    default: null,
  })(contentTypeId(entityProps), { ...props, ...entityProps })

  return { entityProps, mappedProps }
}

export const ConnectedGuide = (props) => {
  const { showSimpleCard, entity, linkText, updatedAtDate } = props
  // FIXME: This is an interface touch point between eevee core & old redux state

  const { entityProps, mappedProps } = usePageProps(props)

  if (showSimpleCard) {
    return <SimpleCategoryGuideCard {...props} {...entityProps} {...mappedProps} />
  }

  const contentEntity = useEntity(entity)
  const imageEntity = useEntity(contentEntity.metaImage)
  const guideContent = {
    title: contentEntity.title,
    description: contentEntity.metaDescription,
    image: { url: imageEntity.file?.url, alt: imageEntity.description },
    linkText: linkText,
    updatedAtDate,
  }

  return props.useCardV2 ? (
    <CardV2 {...{ ...props, guideContent }} {...mappedProps} />
  ) : (
    <Card {...props} {...entityProps} {...mappedProps} />
  )
}

export default ({ guide, linkText, showDate, ...props }) => {
  const connectedEntity = guide?.fields?.variants?.length
    ? guide?.fields?.variants[0]
    : guide?.fields?.entry || guide.fields.link
  const guideCardMeta = useMeta(['guideCard'], {})

  /**
   * Meta settings to configure card display by brand
   *
   * showDescription - Whether to show the meta description in the card
   * showDateMeta        - Whether to show the published date as superscript
   * showLink        - Whether to show the read more link text
   */
  const {
    linkText: linkTextValue = linkText,
    linkIcon = null,
    dateFormat = 'Do MMMM, YYYY',
    showDescription,
    showDate: showDateMeta,
    showLink,
  } = { ...guideCardMeta, ...guideCardMeta[props.variant] }

  const headerIcon = guideCardMeta[props.variant]?.headerIcon
  const headerIconSettings = (
    <If condition={headerIcon}>
      <DynamicSparkIcon
        glyph={headerIcon.glyph}
        size={headerIcon.size}
        direction={headerIcon.direction}
      />
    </If>
  )

  return (
    <ConnectedGuide
      customDate={guide.fields.updatedDate || guide.fields.publishDate}
      entity={connectedEntity}
      url={guide.fields.url}
      pageType={guide.fields.pageType}
      linkIcon={linkIcon}
      linkText={linkTextValue}
      dateFormat={dateFormat}
      showDescription={showDescription !== false}
      showDate={showDate}
      showDateInSuperscript={showDateMeta !== false}
      showLink={showLink !== false}
      headerIconSettings={headerIconSettings}
      {...props}
    />
  )
}
